import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ColDef, GridApi, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { AZ } from "../../generated-models/AZ";
import { CreatePfRequest } from "../../generated-models/CreatePfRequest";
import { CreatePfSubRequest } from "../../generated-models/CreatePfSubRequest";
import { Manatree } from "../../generated-models/Manatree";
import { Region } from "../../generated-models/Region";
import { Team } from "../../generated-models/Team";
import { IUserProfile } from "../../npr-request.model";
import { ApiService } from "../../services";
import { getPfSubRequestColumnDefs, getPfSubRequestDefaultColDef } from "./pf-subrequest-grid";

interface ServiceTreeOption {
  Name?: string;
  Oid: string;
}

@Component({
  standalone: false,
  templateUrl: "./pf-request-create.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
  styles: [
    `
      .request-input {
        box-sizing: border-box;
        width: min(100%, 100ch);
        min-width: 0;
        max-width: 100%;
      }

      :host ::ng-deep .service-tree-select .ng-select-container,
      :host ::ng-deep .region-select .ng-select-container {
        width: 100%;
      }

      :host ::ng-deep .service-tree-select .ng-value,
      :host ::ng-deep .region-select .ng-value {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .request-label {
        font-weight: 700;
        padding-right: 15px;
        text-align: right;
      }

      .subrequest-add-row {
        display: flex;
        align-items: center;
      }

      .subrequest-option-controls {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        min-height: 38px;
      }

      .subrequest-option-modes {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        min-height: 38px;
      }

      .subrequest-option-input {
        flex: 1 1 20rem;
        min-width: 0;
      }

      @media (max-width: 575.98px) {
        .request-label {
          text-align: left;
        }

        .subrequest-add-row {
          display: block;
        }

        .subrequest-option-controls {
          align-items: stretch;
          flex-direction: column;
          gap: 0.5rem;
        }
      }
    `,
  ],
})
export class PfRequestCreateComponent implements OnInit {
  form: FormGroup;
  subRequestForm: FormGroup;
  isLoading = false;
  manatreeTooltip = "If your Manatree is not listed, please contact PF Admins.";
  serviceTreeTooltip = "If your Service Tree ID is not listed, please contact PF Admins.";
  purposeTooltip = "If your Purpose is not listed, please contact PF Admins.";
  requestInformationTooltip = "For any enquiries, please contact PF Admins.";
  zoneOptions: { label: string; value: string }[] = [];
  allServiceTreeOptions: Team[] = [];
  serviceTreeOptions: ServiceTreeOption[] = [];
  purposeOptions = ["Test", "Growth", "Service Migration"];
  manatreeMappings: Manatree[] = [];
  manatreeOptions: string[] = [];
  regionOptions: Region[] = [];
  azEntities: AZ[] = [];
  userProfile: IUserProfile;
  subRequests: CreatePfSubRequest[] = [];
  subRequestGridApi: GridApi;
  hasSelectedSubRequest = false;
  showNoSubRequestError = false;
  subRequestColumnDefs: ColDef[] = getPfSubRequestColumnDefs();
  subRequestDefaultColDef: ColDef = getPfSubRequestDefaultColDef();

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private notificationService: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      serviceTreeId: [null, [Validators.required]],
      manatree: [null, [Validators.required]],
      region: [null, [Validators.required]],
      purpose: [null, [Validators.required]],
    });
    this.subRequestForm = this.fb.group({
      ZoneMode: ["Any", [Validators.required]],
      Zone: [null],
      ClusterMode: ["Any", [Validators.required]],
      Cluster: [""],
      SkuMode: ["Any", [Validators.required]],
      SKU: [""],
      Quantity: [1, [Validators.required, Validators.min(1)]],
    });
  }

  async ngOnInit() {
    try {
      const [userProfile, teams, regions, azEntities, manatreeMappings, approvers] = await Promise.all([
        this.apiService.getUserProfile(),
        this.apiService.getTeamList().toPromise(),
        this.apiService.getRegionList().toPromise(),
        this.apiService.getAzEntity(true).toPromise(),
        this.apiService.getManatreeMappings().toPromise(),
        this.apiService.getPfRequestApprovers().toPromise(),
      ]);
      this.userProfile = userProfile;
      this.allServiceTreeOptions = teams
        .sort((a, b) => a.Name.localeCompare(b.Name) || a.Oid.localeCompare(b.Oid));
      this.azEntities = azEntities ?? [];
      this.manatreeMappings = manatreeMappings ?? [];
      this.manatreeOptions = Array.from(new Set(this.manatreeMappings.map((mapping) => mapping.ManatreeName)))
        .sort((a, b) => a.localeCompare(b));
      this.regionOptions = regions.sort((a, b) => a.RegionName.localeCompare(b.RegionName));
      if (approvers?.length) {
        const pfAdmins = approvers.join(", ");
        this.manatreeTooltip = `If your Manatree is not listed, please contact PF Admins: ${pfAdmins}`;
        this.serviceTreeTooltip = `If your Service Tree ID is not listed, please contact PF Admins: ${pfAdmins}`;
        this.purposeTooltip = `If your Purpose is not listed, please contact PF Admins: ${pfAdmins}`;
        this.requestInformationTooltip = `For any enquiries, please contact PF Admins: ${pfAdmins}`;
      }
      this.cdr.detectChanges();
    } catch (error) {
      this.notificationService.error("Failed to load Manatree, service tree, or region options");
      console.error(error);
    }
  }

  serviceTreeSearch(term: string, team: ServiceTreeOption): boolean {
    const normalizedTerm = term.trim().toLowerCase();

    return (team.Name?.toLowerCase().includes(normalizedTerm) ?? false) || team.Oid.toLowerCase().includes(normalizedTerm);
  }

  onManatreeChanged() {
    const selectedManatree = this.form.get("manatree")?.value as string | null;
    this.form.patchValue({ serviceTreeId: null });
    this.serviceTreeOptions = [];

    if (!selectedManatree) {
      return;
    }

    const associatedServiceTreeIds = Array.from(new Set(
      this.manatreeMappings
        .filter((mapping) => mapping.ManatreeName.toLowerCase() === selectedManatree.toLowerCase())
        .map((mapping) => mapping.ServiceTreeId)
    ));
    this.serviceTreeOptions = associatedServiceTreeIds.map((serviceTreeId) => {
      const team = this.allServiceTreeOptions.find(
        (option) => option.Oid.toLowerCase() === serviceTreeId.toLowerCase()
      );

      return team ?? { Oid: serviceTreeId };
    });

    if (this.serviceTreeOptions.length === 1) {
      this.form.patchValue({ serviceTreeId: this.serviceTreeOptions[0].Oid });
    }
  }

  onRegionChanged() {
    const selectedRegionName = this.form.get("region")?.value as string | null;
    const selectedRegion = this.regionOptions.find(
      (region) => region.RegionName.toLowerCase() === selectedRegionName?.toLowerCase()
    );
    this.zoneOptions = this.getZoneOptions(selectedRegion?.DCMTRegionId);

    const zoneControl = this.subRequestForm.get("Zone");
    if (!this.zoneOptions.some((option) => option.value === zoneControl?.value)) {
      zoneControl?.setValue(null);
    }
  }

  private getZoneOptions(dcmtRegionId?: string | null) {
    if (!dcmtRegionId) {
      return [];
    }

    const normalizedRegionId = dcmtRegionId.toLowerCase();
    // AvailabilityZoneName is the DCMT id, e.g. "uscentral-AZ01"; the dropdown shows the "AZ01" suffix.
    const zones = this.azEntities
      .filter((az) => az.DCMTRegionId?.toLowerCase() === normalizedRegionId)
      .map((az) => az.AvailabilityZoneName?.split("-").pop())
      .filter((zone): zone is string => !!zone);

    return Array.from(new Set(zones))
      .sort((a, b) => a.localeCompare(b))
      .map((zone) => ({ label: zone, value: zone }));
  }

  onSubRequestGridReady(event: GridReadyEvent) {
    this.subRequestGridApi = event.api;
    event.api.sizeColumnsToFit();
  }

  addSubRequest() {
    if (this.subRequestForm.invalid) {
      this.subRequestForm.markAllAsTouched();
      this.notificationService.warning("Please fill in all required subrequest fields");
      return;
    }

    const formValue = this.subRequestForm.value;
    this.subRequests = [
      ...this.subRequests,
      {
        Zone: formValue.ZoneMode === "Any" ? "Any" : formValue.Zone,
        Cluster: formValue.ClusterMode === "Any" ? "Any" : formValue.Cluster.trim(),
        SKU: formValue.SkuMode === "Any" ? "Any" : formValue.SKU.trim(),
        Quantity: Number(formValue.Quantity),
      },
    ];
    this.showNoSubRequestError = false;
    this.subRequestForm.reset({
      ZoneMode: "Any",
      Zone: null,
      ClusterMode: "Any",
      Cluster: "",
      SkuMode: "Any",
      SKU: "",
      Quantity: 1,
    });
    this.onSubRequestOptionModeChanged("ZoneMode", "Zone");
    this.onSubRequestOptionModeChanged("ClusterMode", "Cluster");
    this.onSubRequestOptionModeChanged("SkuMode", "SKU");
  }

  onSubRequestOptionModeChanged(modeControlName: string, valueControlName: string) {
    const valueControl = this.subRequestForm.get(valueControlName);
    if (this.subRequestForm.get(modeControlName)?.value === "Specific") {
      valueControl?.setValidators([Validators.required]);
    } else {
      valueControl?.clearValidators();
      valueControl?.setValue(valueControlName === "Zone" ? null : "");
      valueControl?.markAsUntouched();
      valueControl?.markAsPristine();
    }
    valueControl?.updateValueAndValidity();
  }

  onSpecificOptionBlur(modeControlName: string, valueControlName: string) {
    if (this.subRequestForm.get(modeControlName)?.value === "Specific") {
      this.subRequestForm.get(valueControlName)?.markAsTouched();
    }
  }

  onSubRequestSelectionChanged(event: SelectionChangedEvent) {
    this.hasSelectedSubRequest = event.api.getSelectedRows().length > 0;
  }

  removeSelectedSubRequest() {
    if (!this.hasSelectedSubRequest) {
      return;
    }

    const selectedSubRequest = this.subRequestGridApi.getSelectedRows()[0] as CreatePfSubRequest;
    this.subRequests = this.subRequests.filter((subRequest) => subRequest !== selectedSubRequest);
    this.hasSelectedSubRequest = false;
  }

  async onSubmit() {
    this.subRequestGridApi?.stopEditing();
    this.showNoSubRequestError = this.subRequests.length === 0;

    if (this.form.invalid || this.subRequests.length === 0) {
      this.form.markAllAsTouched();
      this.notificationService.warning("Please fill in all required fields");
      return;
    }

    this.isLoading = true;
    try {
      const formValue = this.form.value;
      const request: CreatePfRequest = {
        ServiceTreeId: formValue.serviceTreeId,
        Manatree: formValue.manatree,
        Region: formValue.region,
        Purpose: formValue.purpose,
        SubRequests: this.subRequests.map((subRequest) => ({
          Zone: subRequest.Zone,
          Cluster: subRequest.Cluster,
          SKU: subRequest.SKU,
          Quantity: Number(subRequest.Quantity),
        })),
      };

      await this.apiService.createPfRequest(request).toPromise();
      this.notificationService.success("PF request created successfully");
      await this.router.navigate(["/pf/ad-hoc-requests/all"]);
    } catch (error: unknown) {
      const errorMessage = typeof error === "string"
        ? error
        : error instanceof Error
          ? error.message
          : "Unknown error";
      this.notificationService.error(`Failed to create PF request: ${errorMessage}`);
      console.error(error);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

}
